import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { ReplaySubject } from 'rxjs/ReplaySubject';

import { ApiService } from './api.service';
import { JwtService } from './jwt.service';
import { CaregiverService } from './caregiver.service';
import { PatientListStateService } from './patient-list-state.service';
import { WorkHistoryListStateService } from './work-history-list-state.service';
import { User } from '../models';
import { distinctUntilChanged, map } from 'rxjs/operators';


@Injectable()
export class UserService {
  private currentUserSubject = new BehaviorSubject<User>({} as User);
  public currentUser = this.currentUserSubject.asObservable().pipe(distinctUntilChanged());

  private isAuthenticatedSubject = new ReplaySubject<boolean>(1);
  public isAuthenticated = this.isAuthenticatedSubject.asObservable();
  public isLoggingOut$ = new BehaviorSubject<boolean>(false);

  constructor(
    private apiService: ApiService,
    private http: HttpClient,
    private jwtService: JwtService,
    private caregiverService: CaregiverService,
    private patientListState: PatientListStateService,
    private workHistoryListState: WorkHistoryListStateService
  ) { }

  // Verify JWT in localstorage with server & load user's info.
  // This runs once on application startup.
  populate() {
    if (this.jwtService.getToken()) {
      this.getMyData()
        .subscribe(
          data => {
            this.setAuth(data.data);
          },
          err => this.purgeAuth()
        );
    } else {
      this.purgeAuth();
    }
  }

  setAuth(user: User) {
    // Token zapisujemy zawsze, gdy odpowiedź go zawiera. Wcześniejszy warunek
    // `if (!this.jwtService.getToken())` powodował, że przy JAKIMKOLWIEK starym wpisie
    // w localStorage świeży token z logowania był po cichu wyrzucany, a aplikacja dalej
    // wysyłała stary — backend odpowiadał wtedy 400 + 'Token is expired' i użytkownik
    // wylatywał tuż po zalogowaniu.
    //
    // Warunek na obecność pola jest konieczny: populate() woła setAuth z danymi
    // /user/me, które tokenu nie muszą zawierać — nadpisanie na ślepo kasowałoby sesję
    // przy każdym odświeżeniu strony.
    if (user && user.authentication_token) {
      this.jwtService.saveToken(user.authentication_token);
    }
    this.currentUserSubject.next(user);
    this.isAuthenticatedSubject.next(true);
  }

  purgeAuth() {
    this.jwtService.destroyToken();
    this.patientListState.clear();
    this.workHistoryListState.clear();
    // Próg nieaktywności jest per konto - kolejny zalogowany nie może dostać cudzego
    this.caregiverService.clearInactivityTimeoutCache();
    this.currentUserSubject.next({} as User);
    this.isAuthenticatedSubject.next(false);
  }

  logout(): Observable<any> {
    return this.apiService.get('/user/logout');
  }

  attemptAuth(type, credentials): Observable<User> {
    return this.apiService.post('/' + type, credentials)
        .pipe(map(
            data => {
              if (!type.includes('register') && data.data.authentication_token) {
                this.setAuth(data.data);
              }
              return data;
            }
        ));
  }

  checkActivationOfAccount(credentials): Observable<any> {
    return this.apiService.get('/user/checkActivationOfAccount', credentials)
  }

  retrivePass(type, credentials): Observable<User> {
    return this.apiService.post('/' + type, credentials);
  }

  getTypeOfUser(): string {
    return this.currentUserSubject.value.type;
  }

  getPrivilegeEko(): boolean {
    if (this.currentUserSubject.value.user_id && this.currentUserSubject.value.user_id.privilege) {
      return this.currentUserSubject.value.user_id.privilege.includes('eko')
    }
  }

  getPrivilegeAssignResponder(): boolean {
    if (this.currentUserSubject.value.user_id && this.currentUserSubject.value.user_id.privilege) {
      return this.currentUserSubject.value.user_id.privilege.includes('assignResponder')
    }
  }

  getCurrentUser(): User {
    return this.currentUserSubject.value;
  }

  getMyPatientCardUrl(): string {
    return `/users/user/${this.currentUserSubject.value.user_id.imei}`;
  }

  getMyPatientSettingsUrl(): string {
    const me = this.currentUserSubject.value.user_id;
    return me.if_indiv_user ? `/indiv-client/edit-user/${me.imei}` : `/users/edit-user/${me.imei}`;
  }

  getRole(): number {
    return this.getIntRole(this.currentUserSubject.value.role);
  }

  /** Dokładne dopasowanie flagi w user.privilege ("flagA;flagB"). */
  hasPrivilege(flag: string): boolean {
    const me: any = this.currentUserSubject.value.user_id || {};
    return String(me.privilege || '').split(';').map((p) => p.trim()).indexOf(flag) !== -1;
  }

  getIntRole(role: string): number {
    switch (role) {
      case 'ROLE_API_USER':
        return 1;
      case 'ROLE_API_CAREGIVER':
        return 2;
      case 'ROLE_API_MANAGER':
        return 3;
      default:
        return 99;
    }
  }

  update(user): Observable<User> {
    return this.apiService
      .put('/user', { user })
      .pipe(map(data => {
        this.currentUserSubject.next(data.user);
        return data.user;
      }));
  }

  generateReport(id, dateFrom, dateTo, lang) {
    return this.apiService.post(`/generate/project/report/${lang}`, { id: id, dateFrom: dateFrom, dateTo: dateTo });
  }

  getMyData(): Observable<any> {
    return this.apiService.get('/user/me');
  }

  // Ponowne pobranie danych zalogowanego użytkownika (/user/me) i rozgłoszenie ich do
  // wszystkich komponentów spiętych z `currentUser`. Bez tego po zapisie profilu w
  // ustawieniach konta w pamięci zostawały stare dane — świeże pojawiały się dopiero
  // po przeładowaniu strony (populate() wykonuje się tylko przy starcie aplikacji).
  // Świadomie NIE używamy tu setAuth(): ono dodatkowo emituje isAuthenticated,
  // co niepotrzebnie odpalałoby ponownie logikę guardów, notyfikacji i route-refresh.
  refreshCurrentUser(): Observable<User> {
    return this.getMyData().pipe(map(data => {
      const user = data && data.data;
      if (user) {
        this.currentUserSubject.next(user);
      }
      return user;
    }));
  }

  /**
   * Czy organizacja pracuje w trybie telecentrum (SDLY-3031). Flaga przychodzi tylko
   * z /user/me — odpowiedź logowania jej nie zawiera, a po logowaniu jest nawigacja bez
   * przeładowania. Bez dociągnięcia pierwszy alarm po zalogowaniu dostałby zwykły
   * formularz. Gdy flagi brak, pobieramy /user/me raz — kolejne wywołania mają ją już
   * w pamięci.
   */
  isTelecentrumMode(): Observable<boolean> {
    const current = this.getCurrentUser();
    if (current && current.telecentrum_mode !== undefined) {
      return of(!!current.telecentrum_mode);
    }
    return this.refreshCurrentUser().pipe(map(user => !!(user && user.telecentrum_mode)));
  }

  getInactivitySettings(): Observable<any> {
    return this.apiService.get('/manager/inactivity-settings');
  }

  updateInactivitySettings(data): Observable<any> {
    return this.apiService.put('/manager/inactivity-settings', data);
  }

  testInactivityMail(): Observable<any> {
    return this.apiService.post('/manager/inactivity-settings/test', {});
  }

  /**
   * Wyłącznik floty dla edycji numerów alarmowych przez podopiecznych.
   * GET zwraca też has_privilege (alarmPhonesControl nadaje admin) — sekcja
   * w ustawieniach i sterowanie na liście podopiecznych pokazują się tylko z nim.
   */
  getAlarmPhonesSettings(): Observable<any> {
    return this.apiService.get('/manager/alarm-phones-settings');
  }

  updateAlarmPhonesSettings(data): Observable<any> {
    return this.apiService.put('/manager/alarm-phones-settings', data);
  }

  setPassword(type, data): Observable<any> {
    return this.apiService.put('/user/' + type, data);
  }

  checkImei(imei): Observable<any> {
    return this.apiService.get('/band/checkImei', { imei: imei });
  }

  checkImeiProjectPesel(imei, email, pesel, lang, firstName?, lastName?): Observable<any> {
    return this.apiService.post('/band/checkImeiProjectPesel', {
      imei: imei,
      email: email,
      pesel: pesel,
      lang: lang,
      firstName: firstName,
      lastName: lastName
    });
  }

  checkImeiProject(imei): Observable<any> {
    return this.apiService.get('/band/checkImeiProject', { imei: imei });
  }

  checkImeiUser(imei): Observable<any> {
    return this.apiService.get('/band/checkImeiUser', { imei: imei });
  }

  checkImeiAndCreateAccount(imei, mail, firstName?, lastName?, chosenCountry?): Observable<any> {
    return this.apiService.get('/band/checkImeiAndCreateAccount', {
      imei: imei,
      mail: mail,
      firstName: firstName,
      lastName: lastName,
      chosenCountry: chosenCountry,
    });
  }

  changeImei(data): Observable<any> {
    return this.apiService.post('/manager/changeImei', data);
  }

  checkEmail(email): Observable<any> {
    return this.apiService.get('/user/emailCheck', { email: email });
  }

  checkErpRegisterEmail(email): Observable<any> {
    return this.apiService.get('/user/checkErpRegisterEmail', { email: email });
  }

  getAddress(imei): Observable<any> {
    return this.apiService.get(`/user/getAddress/${imei}`);
  }

  getObservers(patientImei?: number | string): Observable<any> {
    const qs = patientImei ? `?patientImei=${patientImei}` : '';
    return this.apiService.get(`/user/getObservers${qs}`);
  }

  addObserver(data): Observable<any> {
    return this.apiService.post(`/user/addObserver`, data);
  }

  editObserver(observer): Observable<any> {
    return this.apiService.put(`/user/updateObserver/${observer.id}`, observer);
  }

  getObserver(id): Observable<any> {
    return this.apiService.get(`/user/showObserver/${id}`);
  }

  deleteObserver(observer_id): Observable<any> {
    return this.apiService.delete(`/user/deleteObserver/${observer_id}`);
  }

  setStatusOfCreate(imei): Observable<any> {
    return this.apiService.postV2(`/user/setStatusOfCreate/${imei}`)
  }

  checkAllowChangingOnFirstLogin(hash): Observable<any> {
    return this.apiService.getDefaultController(`/checkAllowChangingOnFirstLogin/${hash}`);
  }

  getVersionApi(): Observable<any> {
    return this.apiService.getDefaultController(`/getVersionApi`);
  }

  getResponderState(): Observable<any> {
    return this.apiService.get(`/user/responderState`);
  }

  getManagerPrivilege(user_id): Observable<any> {
    return this.apiService.get(`/caregiver/getManagerPrivilege/${user_id}`);
  }

  responderEndIntervention(imei): Observable<any> {
    return this.apiService.post(`/user/responderEndIntervention`, { imei: imei });
  }

  editManagerProfile(id, data): Observable<any> {
    return this.apiService.put(`/user/editManagerProfile/${id}`, data);
  }

  editCaregiverProfile(id, data): Observable<any> {
    return this.apiService.put(`/user/editCaregiverProfile/${id}`, data);
  }

  getFcmNotificationSettings(): Observable<any> {
    return this.apiService.get(`/user/fcm-notification-settings`);
  }

  updateFcmNotificationSettings(settings: { [key: string]: boolean }): Observable<any> {
    return this.apiService.patch(`/user/fcm-notification-settings`, settings);
  }

}
